import {
  ExpectationLayoutType,
  ExpectationMetadata,
  ExpectationType,
  LoadingState,
  Threshold
} from '@common/DataQuality/dataQualityTypes';
import { UISpecCategory } from '@common/enums';
import { SqlProjectContentQueryResponse } from '@common/queries/sqlProjectRelations';
import { LSP } from '@LSP/base/types';
import { BackEndCodeLanguageType, ForkModeType, SyncModeType } from '@prophecy/graphql-types/enums';
import { ArrayElement, ExtractArrayType, ValueOf } from '@prophecy/interfaces/generic';
import { SubflowPresetId } from '@prophecy/ui-v3/Graph/colorPresets';
import { EdgeData, NodeData } from '@prophecy/ui-v3/Graph/types';
import { Edge, Node } from 'reactflow';

import { ProviderType } from '../../schema/types';
import { v1DataExplorerType } from '../interims/conditionTypes';
import { DialogCopilotProps } from './copilotCoreTypes';
import { DependencyType } from './dependencyTypes';
import { Entity } from './Entity';
import { DidActionPayload, DidChangePayload, DidUpdatePayload, WindowMessage } from './messageTypes';
import { UISpecElement } from './specTypes';

import type { Port } from '../../redux/portTypes';
import type { CopilotState } from '../Copilot/types';
import type { Diagnostic } from '@LSP/types';

export type DiagnosticsMap = { [key: string]: Diagnostic[] };
export type GraphElements = (Node<NodeData> | Edge<EdgeData>)[];
export type CopilotAgentState = {};
interface ExternalSyncStatus {
  state: string;
  displayState: string;
  displayMessage: string;
}

export interface Project {
  id: string;
  name: string;
  forkMode: ForkModeType;
  syncMode: SyncModeType;
  externalSyncStatus: ExternalSyncStatus;
  externalOriginUri?: string;
  mainBranch: string;
  providerType?: ProviderType;
  language: BackEndCodeLanguageType;
  teamId: string;
  workingBranch: {
    name: string;
  };
}

export interface Projects {
  id: string;
  label: string;
  pipelines: Pipeline[];
}

export interface Pipeline {
  id: string;
  label: string;
  value: string;
}

export interface ExampleProject {
  _id: string;
  name: string;
  sqlOrchestratorPipelines: ExtractArrayType<
    NonNullable<SqlProjectContentQueryResponse['Project']>['sqlOrchestratorPipelines']
  >[];
  providerType?: ProviderType;
}

export type Connection = {
  id: string;
  source: string;
  sourcePort: string;
  target: string;
  targetPort: string;
  visualProperty?: { isHidden: boolean };
  isWireless?: boolean;
};

export interface BaseProcessMetadata {
  label: string;
  comment?: string;
  autoUpdateComment?: boolean;
  passThrough?: string;
  removalCondition?: string;
  isLabelGrayed?: boolean;
  slug: string;
  hasWelcomePage?: boolean;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  isAutoLayoutEnabled?: boolean;
  dataExplorerProps?: v1DataExplorerType;
  visualProperty?: { isHidden: boolean };
  phase?: number;
}

export type ParamConfig = {
  configName: string;
  propertyPath: string;
};

export type DiffProperties = {
  configured: boolean;
  keyColumns: string[];
  sourceType: string;
  showMismatchRows?: boolean;
  catalogTable?: {
    catalog: string;
    schema: string;
    table: string;
    isCatalogEnabled?: boolean;
  };
} & { [key: string]: unknown };

export interface BaseProcess<PM extends BaseProcessMetadata, Prop = object> extends BaseGemType {
  id: string;
  properties: Prop;
  paramProperties?: ParamConfig[];
  metadata: PM;
  parent?: string;
  visualMetaInfo?: { visualProcessIds?: string[] };
  ports?: {
    isCustomOutputSchema?: boolean;
    autoUpdateOnRun?: boolean;
    inputs?: Port[];
    outputs?: Port[];
  };
  diff?: DiffProperties;
}

export interface BaseMetaInfo {
  frontEndLanguage: BackEndCodeLanguageType;
  language: BackEndCodeLanguageType;
  // TODO: make it optional as fabricId is not present at al times
  fabricId: string;
}

export type GenericProcess<PM extends BaseProcessMetadata, P extends BaseProcess<PM>> = P;

export type GenericGraphProcess<GM, PM extends BaseProcessMetadata, P extends BaseProcess<PM>, C extends Connection> =
  | GenericProcess<PM, P>
  | GenericSubGraph<GM, PM, P, C>;
export type GenericGraphProcesses<
  GM,
  PM extends BaseProcessMetadata,
  P extends BaseProcess<PM>,
  C extends Connection
> = {
  [key: string]: GenericGraphProcess<GM, PM, P, C>;
};
export type GenericSubGraph<
  GM extends unknown,
  PM extends BaseProcessMetadata,
  P extends BaseProcess<PM>,
  C extends Connection
> = GenericProcess<PM, P> & {
  processes: GenericGraphProcesses<GM, PM, P, C>;
  connections: C[];
  metainfo: GM;
};

export type GenericGraph<
  GM extends unknown,
  PM extends BaseProcessMetadata,
  P extends BaseProcess<PM>,
  C extends Connection
> = GenericSubGraph<GM, PM, P, C>;

export type GenericGraphProcessType<G extends CommonGraph> = ValueOf<G['processes']>;
export type Process = GenericGraphProcess<unknown, BaseProcessMetadata, BaseProcess<BaseProcessMetadata>, Connection>;
export type Job = {
  label: string;
  value: string;
  enabled: boolean;
  schedule: string;
  scheduleHelp: string;
  targetEntity?: Entity;
  targetEntityName?: string;
};
export type Metadata = {
  projectId?: string;
  projectName?: string;
  configTopLevelPackage?: string;
  project?: Project;
  projects?: Projects[];
  jobs?: Job[];
  isDatasetInfo?: boolean;
  schedulers?: { label: string; value: { fabricName: string; scheduler: string; fabricId: string } }[];
  workflowMeta?: {
    workflowUID: string;
    workflowId: string;
    workflowName: string;
  };
  time_zones?: Array<{ label: string; value: string }>;
};
export enum EditMode {
  disabled = 'disabled',
  enabled = 'enabled',
  configuration = 'configuration'
}
export type IDE_MODE_TYPE = `${EditMode}`;
export interface BaseState<G extends CommonGraph, M = Metadata> {
  branch?: string;
  editMode: IDE_MODE_TYPE;
  copilot?: CopilotState;
  copilotAgent?: CopilotAgentState;
  historical?: boolean;
  banner: string;
  root: string;
  gemSpecs: GemSpec[];
  $graph?: G;
  loader: boolean;
  loaderCount: number;
  currentGraph?: G;
  graphPath: string;
  /**
   * Current component id represents the current opened process
   * As process dialog can be spec based or custom (non spec based) like dataset dialog,
   * we also keep separate state to know if spec based process dialog is opened or not
   */
  currentComponentId?: string;
  processSpecDialogOpen?: boolean;
  skipHistory?: boolean;
  metadata: M;
  expectation?: ExpectationMetadata;
  selectedFabricId?: string;
  isInitializing?: boolean;
}

export type CommonGraph = GenericGraph<unknown, BaseProcessMetadata, BaseProcess<BaseProcessMetadata>, Connection>;

export type CommonState = BaseState<CommonGraph>;

export enum CommonActionTypes {
  dialogOpen = '@dialog.open',
  dialogClose = '@dialog.close',
  currentComponentId = '@dialog.currentComponentId',
  currentGraphPath = '@canvas.graphPath',
  loaderOn = 'loaderOn',
  loaderOff = 'loaderOff',
  componentDidOpen = 'component/DidOpen',
  propertyDidUpdate = 'properties/DidUpdate',
  propertiesDidMasterUpdate = 'properties/DidMasterUpdate',
  propertiesDidSchemaUpdate = 'properties/DidSchemaUpdate',
  windowShowMessage = 'window/showMessage',
  initialize = 'initialize',
  $updateState = '$updateState',

  //Expectation actions
  definitionLoader = 'definitionLoader',
  setExpectationDefinition = 'setExpectationDefinition',
  setExpectationUISpec = 'setExpectationUISpec',
  setExpectationDefaultInterval = 'setExpectationDefaultInterval',

  toggleVisualCodeButton = 'toggleVisualCodeButton',

  // copilot actions
  updateCopilotState = 'updateCopilotState',

  // dependencies
  dependenciesDidResolve = 'dependenciesDidResolve',
  featuresDidOpen = 'featuresDidOpen',

  setFabricId = 'setFabricId'
}

export type COMMON_ACTION_TYPES<T extends CommonGraph, M = Metadata, S = BaseState<T, M>> =
  | {
      type: CommonActionTypes.dialogOpen;
      payload: { componentId: string };
    }
  | {
      type: CommonActionTypes.currentComponentId;
      payload: { componentId?: string };
    }
  | { type: CommonActionTypes.currentGraphPath; payload: string }
  | { type: CommonActionTypes.dialogClose }
  | {
      type: CommonActionTypes.loaderOff;
      payload?: { isInitializing?: boolean; resetLoaderCount?: boolean };
    }
  | { type: CommonActionTypes.toggleVisualCodeButton; payload: boolean }
  | { type: CommonActionTypes.loaderOn; payload?: { isInitializing?: boolean; hasCountSame?: boolean } }
  | { type: CommonActionTypes.componentDidOpen; payload: GemSpec[] }
  | {
      type: LSP.Method.propertiesDidChange;
      payload: DidChangePayload;
    }
  | { type: LSP.Method.propertiesDidSave; payload?: { isAwaitedSave?: boolean } }
  | {
      type: CommonActionTypes.propertyDidUpdate;
      savePending?: boolean;
      payload: DidUpdatePayload;
    }
  | {
      type: CommonActionTypes.propertiesDidMasterUpdate;
      payload: DidUpdatePayload;
    }
  | {
      type: CommonActionTypes.propertiesDidSchemaUpdate;
      payload: { compiledOnDirtyState: boolean; changes: DidUpdatePayload };
      savePending?: boolean;
    }
  | { type: LSP.Method.propertiesDidReset }
  | { type: LSP.Method.propertiesDidAction; payload: DidActionPayload }
  | { type: LSP.Method.metadataDidReload }
  | { type: CommonActionTypes.$updateState; payload: Partial<S> }
  | {
      type: CommonActionTypes.initialize;
      payload: { editMode: EditMode; banner: string; historical: boolean; sessionId: string };
    }
  | {
      type: CommonActionTypes.setExpectationDefinition;
      payload: { thresholds: Threshold[]; expectationTypes: ExpectationType[] };
    }
  | {
      type: CommonActionTypes.definitionLoader;
      payload: { loading: boolean; key?: keyof LoadingState };
    }
  | { type: CommonActionTypes.setExpectationUISpec; payload: ExpectationLayoutType }
  | { type: CommonActionTypes.setExpectationDefaultInterval; payload: string }
  | { type: CommonActionTypes.updateCopilotState; payload: Partial<CopilotState>; replace?: boolean }
  | { type: CommonActionTypes.dependenciesDidResolve; payload: DependencyType[] }
  | { type: CommonActionTypes.featuresDidOpen; payload: FeatureDidOpen }
  | { type: CommonActionTypes.setFabricId; payload: { fabricId: string | undefined } }
  | { type: CommonActionTypes.windowShowMessage; payload: WindowMessage };

export enum SpecLanguage {
  python = 'python',
  scala = 'scala'
}

export enum AirflowSpecLanguage {
  python = 'python'
}

export type SubgraphConnectionMap = Record<
  string,
  {
    source: BaseProcess<BaseProcessMetadata>;
    sourcePort: string;
    targetPort: string;
  }
>;

export type GraphMetadata<T = CommonGraph> = {
  graph: T;
  graphPath: string;
  rootGraph: T;
  rootPath: string;
  readOnly?: boolean;
};

export type ExecutionError = {
  msg?: string;
  trace?: string | string[];
  data?: string;
};
export enum ProjectSourceType {
  imported = 'imported',
  new = 'new'
}
export enum PrivilegeType {
  Subscriber = 'Subscriber',
  Author = 'Author'
}

export type BaseSourceSuggestion = {
  sourceLabel: string;
  canJoinWith: {
    processLabel: string;
    processId: string;
    properties: {
      columnsSelector: string[];
      conditions: {
        alias: string;
        expression: string;
        joinType: string;
      }[];
      expressions: {
        alias: string;
        expression: string;
      }[];
      headAlias: string;
    };
    isRecommended: boolean;
    joinColumns: string[];
  }[];
};
export type SparkSourceSuggestion = {
  sourceDatasetId: string;
} & BaseSourceSuggestion;
export type SourceSuggestionsReferenceProcess = ArrayElement<BaseSourceSuggestion['canJoinWith']>;

export type FeatureDidOpen = {
  [feature: string]: {
    [subfeature: string]: {
      enabled: boolean;
      reason?: string;
      resolution?: string;
    };
  };
};
export type VisualGroupProperties = {
  isCollapsed: boolean;
  hideOptions: boolean;
  colorPreset?: SubflowPresetId;
  actualDimensions?: {
    width: number;
    height: number;
  };
};

export type DatabricksJobInfo = {
  jobId: number;
  jobName: string;
  creatorUserName?: string;
  createdTime: number;
  tags: Record<string, string>;
  timeoutSeconds?: number;
  maxConcurrentRuns?: number;
  format?: string;
  hasSchedule: boolean;
  hasTrigger: boolean;
  settingsJson: string;
};

export type BindingType = Record<string, string>;

export enum STATE_SCHEMA_TYPES {
  string = 'string',
  int = 'int',
  float = 'float',
  column = 'column',
  boolean = 'boolean',
  struct = 'struct',
  list = 'list',
  scolumn = 'scolumn',
  optional = 'optional',
  union = 'union',
  uiexpression = 'uiexpression',
  expression = 'expression'
}

type PrimitiveSpec = {
  kind: Omit<STATE_SCHEMA_TYPES, 'list' | 'struct'>;
  name: string;
  default?: string | boolean | number | null;
};

export type ListSpec = {
  kind: STATE_SCHEMA_TYPES.list;
  element: Specs;
  name: string;
};

type UnionElementSpec = {
  contains: Specs;
  metadata: unknown;
};

export type UnionSpec = {
  kind: STATE_SCHEMA_TYPES.union;
  name: string;
  elements: UnionElementSpec[];
};

export type OptionalSpec = {
  kind: STATE_SCHEMA_TYPES.optional;
  element: Specs;
};

type Field = {
  name: string;
  contains: Specs;
  required: boolean;
  default?: string | boolean | number | null;
  metadata?: unknown;
};

export type StructSpec = {
  name: string;
  kind: STATE_SCHEMA_TYPES;
  fields: Field[];
};

export type Specs = StructSpec | ListSpec | PrimitiveSpec | UnionSpec | OptionalSpec;

export type StateSchema = StructSpec;

export enum GemSpecType {
  ComponentGem = 'ComponentGem',
  MetaComponentGem = 'MetaComponentGem'
}

export interface ComponentInfo {
  gemId: string;
  version?: string;
  projectId: string;
  projectName: string;
}

export type GemSpec = {
  kind: string;
  label?: string;
  isHidden?: boolean;
  isBroken?: boolean;
  category: UISpecCategory;
  gemType: GemSpecType;
  uiSpec: UISpecElement;
  defaultState: GenericGraphProcess<unknown, BaseProcessMetadata, BaseProcess<BaseProcessMetadata>, Connection>;
  stateSchema: StateSchema;
  specCode: string;
  uri: string;
  helpDocument?: string;
  gemDescription?: string;
  docUrl?: string;
  componentInfo?: ComponentInfo;
  showNestedColumnsCount?: boolean;
  source: string;
  supportPassThrough?: boolean;
  altProcessLabel?: string;
  visualProperty?: {
    metaGemInterimRowName: string;
  };
  copilot?: DialogCopilotProps;
  projectName?: string;
  isTransformationGem?: boolean;
  disabled?: boolean;
  tooltip?: string;
};

export type BaseGemType = {
  component: string;
  kind?: string; // used in sql source/target
  componentInfo?: ComponentInfo;
  category?: string;
};
export type GemType = BaseGemType & {
  subCategory?: string;
  isTransformationGem?: boolean;
};
export type SubGraphSpec = Omit<GemSpec, 'visualProperty'> & {
  originProjectName: string;
  isReadOnly: boolean;
  version: string;
  visualProperty?: {
    reusableSubgraphType: string;
  };
};
